/**
 * Load plugin UIs from an Engine Gateway's HTTP endpoints.
 *
 * Flow:
 *   1. Fetch /api/plugins/ui-manifest.json → list of plugins with UI bundles
 *   2. For each plugin, fetch source text from /api/plugins/:name/ui.js
 *   3. Load via importModuleFromSource (Blob URL → import) to avoid
 *      cross-origin import() failures in Tauri WKWebView
 *   4. Register each PluginUI in the plugin-ui-registry
 */

import type { PluginSourceInfo } from "@magic-md/plugin-sdk";
import { useLogStore } from "../store/log-store";
import { frontendLog } from "../utils/frontend-logger";
import { importModuleFromSource, withPluginStyles } from "./plugin-loader";
import {
    beginPluginUILoad,
    endPluginUILoad,
    pluginUIRegistry,
    registerPluginUIs,
    updatePluginUILoad,
} from "./plugin-ui-registry";

export interface PluginManifestEntry {
    name: string;
    version: string;
    hasUI: boolean;
    hasCSS: boolean;
    uiHash: string;
    cssHash?: string;
    source?: PluginSourceInfo;
}

export interface UIManifestResponse {
    plugins: PluginManifestEntry[];
}

export interface GatewayPluginOptions {
    /**
     * Custom fetch function for environments where standard fetch() doesn't work
     * (e.g. Tauri WKWebView on iOS needs tauriFetch to bypass CORS/ATS).
     * Defaults to globalThis.fetch.
     */
    fetchFn?: typeof globalThis.fetch;
    /**
     * Restrict loaded plugin UIs to specific source kinds. Missing source
     * metadata is treated as a local plugin for backwards compatibility.
     */
    sourceKinds?: Array<NonNullable<PluginSourceInfo["kind"]>>;
    /** Whether to warn when the filtered Gateway manifest contains no plugins. */
    warnIfEmpty?: boolean;
}

export function filterGatewayPluginManifestEntries(
    plugins: readonly PluginManifestEntry[],
    sourceKinds?: readonly NonNullable<PluginSourceInfo["kind"]>[],
): PluginManifestEntry[] {
    const sourceKindSet = sourceKinds ? new Set(sourceKinds) : null;
    return plugins.filter((p) => {
        if (!p.hasUI) return false;
        if (!sourceKindSet) return true;
        return sourceKindSet.has(p.source?.kind ?? "local");
    });
}

/**
 * Fetch the plugin UI manifest from an Engine Gateway.
 *
 * @param gatewayUrl — Base URL of the Gateway (e.g. "http://127.0.0.1:3721")
 * @param options — Optional configuration (e.g. custom fetchFn)
 * @returns The manifest response, or null if fetch failed
 */
export async function fetchPluginManifest(
    gatewayUrl: string,
    options?: GatewayPluginOptions,
): Promise<UIManifestResponse | null> {
    const log = useLogStore.getState();
    const baseUrl = gatewayUrl.replace(/\/+$/, "");
    const doFetch = options?.fetchFn ?? globalThis.fetch.bind(globalThis);

    log.info(`Fetching plugin manifest from ${baseUrl}/api/plugins/ui-manifest.json`, {
        source: "system",
    });

    try {
        const res = await doFetch(`${baseUrl}/api/plugins/ui-manifest.json`);
        if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return (await res.json()) as UIManifestResponse;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        log.error(`Failed to fetch plugin manifest: ${msg}`, {
            source: "system",
        });
        return null;
    }
}

/**
 * Load all plugin UI bundles from an Engine Gateway.
 *
 * @param gatewayUrl — Base URL of the Gateway (e.g. "http://127.0.0.1:3721")
 * @param options — Optional configuration (e.g. custom fetchFn)
 * @returns loaded/failed plugin names
 */
export async function loadGatewayPlugins(
    gatewayUrl: string,
    options?: GatewayPluginOptions,
): Promise<{ loaded: string[]; failed: string[] }> {
    const log = useLogStore.getState();
    const baseUrl = gatewayUrl.replace(/\/+$/, "");
    const scope = `gateway:${baseUrl}:${options?.sourceKinds?.join(",") ?? "all"}`;
    beginPluginUILoad(scope);

    try {
        const manifest = await fetchPluginManifest(gatewayUrl, options);
        if (!manifest) {
            return { loaded: [], failed: [] };
        }

        const uiPlugins = filterGatewayPluginManifestEntries(
            manifest.plugins,
            options?.sourceKinds,
        );
        updatePluginUILoad(scope, uiPlugins.map((p) => p.name));
        if (uiPlugins.length === 0) {
            if (options?.warnIfEmpty !== false) {
                const suffix = options?.sourceKinds
                    ? ` for source kinds: ${options.sourceKinds.join(", ")}`
                    : "";
                log.warn(`No plugins with UI found on Gateway${suffix}`, { source: "system" });
            }
            return { loaded: [], failed: [] };
        }

        log.info(
            `Found ${uiPlugins.length} plugins with UI: ${uiPlugins.map((p) => p.name).join(", ")}`,
            { source: "system" },
        );

        // 2. Fetch source text & load each plugin via Blob URL
        const doFetchBundle = options?.fetchFn ?? globalThis.fetch.bind(globalThis);
        const loaded: string[] = [];
        const failed: string[] = [];

        const results = await Promise.allSettled(
            uiPlugins.map(async (p) => {
                const url = `${baseUrl}/api/plugins/${p.name}/ui.js`;
                const res = await doFetchBundle(url);
                if (!res.ok) {
                    throw new Error(`HTTP ${res.status} fetching ${url}`);
                }
                const source = await res.text();
                let pluginUI = await importModuleFromSource(source, p.name);

                if (p.hasCSS) {
                    const cssUrl = `${baseUrl}/api/plugins/${p.name}/ui.css`;
                    const cssRes = await doFetchBundle(cssUrl);
                    if (!cssRes.ok) {
                        throw new Error(`HTTP ${cssRes.status} fetching ${cssUrl}`);
                    }
                    pluginUI = withPluginStyles(pluginUI, await cssRes.text());
                }

                return {
                    ...pluginUI,
                    source: p.source ?? pluginUI.source ?? { kind: "local" },
                };
            }),
        );

        const loadedPlugins = [];
        for (const [i, result] of results.entries()) {
            if (result.status === "fulfilled") {
                loadedPlugins.push(result.value);
                loaded.push(result.value.manifest.name);
            } else {
                failed.push(uiPlugins[i].name);
                frontendLog.error(
                    `[plugin-loader] Failed to load gateway plugin ${uiPlugins[i].name}`,
                    result.reason,
                    { source: "plugin" },
                );
            }
        }
        registerPluginUIs(loadedPlugins);

        if (failed.length > 0) {
            log.warn(
                `Failed to load ${failed.length} plugins: ${failed.join(", ")}`,
                { source: "system" },
            );
        }

        log.info(
            `Loaded ${pluginUIRegistry.names().length} gateway plugins: ${pluginUIRegistry.names().join(", ")}`,
            { source: "system" },
        );

        return { loaded, failed };
    } finally {
        endPluginUILoad(scope);
    }
}
